import { RtcStatsSnapshot } from './types'

/**
 * Extract the audio-quality signals we track from an `RTCStatsReport`.
 *
 * We read the audio `inbound-rtp` entry (what we hear), the matching
 * `remote-inbound-rtp` (RTT/far-end loss as the far end sees us), and the
 * nominated `candidate-pair` (authoritative RTT). RTT preference order:
 * candidate-pair → remote-inbound-rtp.
 *
 * Pure and defensive: tolerates missing fields/entries and never throws.
 */
export const parseRtcStats = (report: RTCStatsReport): RtcStatsSnapshot => {
  const snapshot: RtcStatsSnapshot = {
    jitterMs: 0,
    rttMs: 0,
    packetsLost: 0,
    packetsReceived: 0,
    concealedSamples: 0,
    totalSamplesReceived: 0,
  }

  let candidatePairRttMs: number | undefined
  let remoteInboundRttMs: number | undefined

  report.forEach((stat: Record<string, unknown>) => {
    const type = stat.type as string | undefined
    const kind = (stat.kind ?? stat.mediaType) as string | undefined

    switch (type) {
      case 'inbound-rtp': {
        // Require an explicit audio kind — skip video and any entry missing
        // kind/mediaType, so a non-audio stream can't clobber audio metrics.
        if (kind !== 'audio') break
        snapshot.jitterMs = num(stat.jitter) * 1000
        // packetsLost can be negative per spec (duplicate/late packets); clamp
        // so cumulative counters stay monotonic for the downstream delta math.
        snapshot.packetsLost = Math.max(0, num(stat.packetsLost))
        snapshot.packetsReceived = num(stat.packetsReceived)
        snapshot.concealedSamples = num(stat.concealedSamples)
        snapshot.totalSamplesReceived = num(stat.totalSamplesReceived)
        break
      }
      case 'remote-inbound-rtp': {
        if (kind !== 'audio') break
        if (stat.roundTripTime != null) {
          remoteInboundRttMs = num(stat.roundTripTime) * 1000
        }
        break
      }
      case 'candidate-pair': {
        // Only trust the nominated/selected pair.
        if (stat.nominated !== true && stat.selected !== true) break
        if (stat.currentRoundTripTime != null) {
          candidatePairRttMs = num(stat.currentRoundTripTime) * 1000
        }
        break
      }
    }
  })

  snapshot.rttMs = candidatePairRttMs ?? remoteInboundRttMs ?? 0
  return snapshot
}

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0
